
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State, toODataString } from '@progress/kendo-data-query';
import { BehaviorSubject, Observable, zip as observableZip, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export abstract class PxdGridEditService extends BehaviorSubject<GridDataResult> {

  public environment = inject(PlexEnvironment);
  public http = inject(HttpClient);
  private errorService = inject(ErrorService);

  public selectChange: boolean = false;

  private responseMessage: Subject<any> = new Subject;
  private data = new DataResult();
  private originalData = new DataResult();
  public createdItems: any[] = [];
  public updatedItems: any[] = [];
  public deletedItems: any[] = [];
  private state: State;
  private BASE_URL = `${this.environment.apiUrl}`;
  private url = `${this.BASE_URL}${this.resource}`;
  private queryString: string = '';
  public loading: boolean = false;

  constructor(
    private resource: string,
    private keys: Array<string>,
  ) {
    super(null);
  }

  public read(state: State, queryString = '') {
    this.loading = true;
    const self = this;
    this.state = state;
    this.queryString = queryString;

    this.fetch().pipe(
      tap(data => { this.data = new DataResult(self.cloneData(data.value), data.total); }),
      tap(data => this.originalData = new DataResult(self.cloneData(data.value), data.total)),)
      .subscribe(data => {
        super.next(data);
        this.loading = false;
      });
  }

  public create(item: any): void {
    this.createdItems.push(item);
    this.data.unshift(item);
    super.next(this.data);
  }

  public update(item: any): void {
    if (!this.isNew(item)) {
      const index = this.itemIndex(item, this.updatedItems);
      if (index !== -1)
        this.updatedItems.splice(index, 1, item);
      else
        this.updatedItems.push(item);
    } else {
      if (this.createdItems.length < 1) {
        this.createdItems.push(item);
      } else {
        this.createdItems.splice(0, 1, item);
      }
    }
  }

  public remove(item: any): void {
    let index = this.itemIndex(item, this.data.value);
    this.data.splice(index, 1);

    index = this.itemIndex(item, this.createdItems);

    if (index >= 0) {
      this.createdItems.splice(index, 1);
    }
    else {
      //in the case of an item that was just created but has not been modified at all, it will not exist in the createdItems array.
      //because of this, we need to make sure it isn't new before queuing for deletion.
      if (!this.isNew(item)) {
        this.deletedItems.push(item);
      }
    }

    index = this.itemIndex(item, this.updatedItems);
    if (index >= 0)
      this.updatedItems.splice(index, 1);

    super.next(this.data);
  }

  public isNew(item: any): boolean {
    return this.keys.every(x => !item[x] || item[x] === 0);
  }

  public hasChanges(): boolean {
    return Boolean(this.deletedItems.length || this.updatedItems.length || this.createdItems.length || this.selectChange);
  }

  public hasItems(): boolean {
    return Boolean(this.data.length);
  }

  public updateItem(item: any) {
    this.updatedItems.push(JSON.parse(JSON.stringify(item)));
  }

  public saveChanges(): Observable<any> {
    if (!this.hasChanges()) return;
    this.loading = true;
    const completed = [];

    this.deletedItems.forEach(item => {
      let uri = `${this.url}(${this.keys.map(key => `${key}=${item[key]}`).join(',')})`; // e.g. /odata/Orders?CustomerId=3&OrderId=7
      completed.push(this.http.delete(uri));
    });

    this.updatedItems.forEach(item => {
      let uri = `${this.url}(${this.keys.map(key => `${key}=${item[key]}`).join(',')})`; // e.g. /odata/Orders(CustomerId=3,OrderId=7)
      completed.push(this.http.patch(uri, item));
    });

    this.createdItems.forEach(item => {
      let uri = `${this.url}`; // e.g. /odata/Orders
      completed.push(this.http.post(uri, item));
    });

    this.reset();

    observableZip(...completed).subscribe((response) => {
      this.read(this.state, this.queryString);
      this.responseMessage.next(response);
    },
      (error) => this.errorService.error(error));

    return this.responseMessage;
  }

  public cancelChanges(): void {
    const self = this;
    this.reset();
    this.data = this.originalData;
    this.originalData = new DataResult(self.cloneData(this.originalData.value), this.originalData.total);
    super.next(this.data);
  }

  public assignValues(target: any, source: any): void {
    Object.assign(target, source);
  }

  private reset() {
    this.data = new DataResult();
    this.deletedItems = [];
    this.updatedItems = [];
    this.createdItems = [];
  }

  private fetch(): Observable<DataResult> {
    return this.http
      .get(`${this.url}?${this.oDataQueryString()}`).pipe(
        map((response) => {
          let data = (response as any).value;
          let total = parseInt(response['@odata.count'], 10);
          return new DataResult(data, total);
        }));
  }

  private oDataQueryString(takeAll?: boolean): string {
    let newState: State = {};
    this.assignValues(newState, this.state);

    if (takeAll) {
      delete newState.skip;
      delete newState.take;
    }

    if (this.queryString.search('filter') !== -1 && this.state.filter && this.state.filter.filters.length > 0) {
      let filterState: State = {};

      newState.filter = null;
      filterState.filter = this.state.filter;

      let newStateQueryString = toODataString(newState);
      let newFilterQueryString = toODataString(filterState);
      let newFilterString = this.queryString.replace('$filter=', newFilterQueryString + ' and ');

      return `${newStateQueryString}&$count=true${newFilterString}`;
    } else {
      return `${toODataString(newState)}&$count=true${this.queryString}`;
    }
  }

  cloneData = (data) => data.map(item => Object.assign({}, item));

  itemIndex = (item: any, data: any[]): number => {
    for (let idx = 0; idx < data.length; idx++) {
      if (this.keys.every(key => data[idx][key] === item[key])) {
        return idx;
      }
    }
    return -1;
  }

  public checkCellType(type: string) {

  }

  public geoEditable(test: any) {
    return test;
  }

  public valueChange(event, method) {
    return this[method](event);
  }

  public allData = (): Observable<DataResult> => {
    return this.http
      .get(`${this.url}?${this.oDataQueryString(true)}`).pipe(
        map((response) => {
          let data = (response as any).value;
          let total = parseInt(response['@odata.count'], 10);
          return new DataResult(data, total);
        }));
  }
}

class DataResult implements GridDataResult {
  data = [];
  total = 0;

  constructor(data?: any[], total?: number) {
    this.data = data || [];
    this.total = total || 0;
  }
  unshift = (item) => {
    this.data.unshift(item); this.total++;
  }
  splice = (index, item) => {
    this.data.splice(index, item); this.total--;
  }
  get length() { return this.data.length; }
  map = (x) => this.data.map(x);
  get value() { return this.data; }
}